import type { AgeTransitionEvent, RoomSnapshot } from '../shared/types';
type Baseline=Pick<RoomSnapshot,'code'|'stageIndex'|'revision'|'phase'>;

/** Connection-local event gate. A first snapshot never replays a chapter. */
export class AgeTransitionTracker {
  private baseline:Baseline|null=null;
  private seen=new Set<string>();
  reset():void {this.baseline=null;this.seen.clear();}
  openingFromSnapshot(room:RoomSnapshot):AgeTransitionEvent|null {
    const previous=this.baseline;
    // The opening belongs to the last confirmed hand, not the Start button.
    // This also covers a missing notification; its ID matches the server event
    // so receiving both cannot restart the animation. Reconnect has no baseline.
    if(!previous || previous.code!==room.code || previous.phase!=='initial-selection' || room.phase!=='setback' || room.stageIndex!==0 || !room.players.length || !room.players.every(player=>player.initialConfirmed))return null;
    const event:AgeTransitionEvent={id:`age:${room.code}:0:${room.revision}`,roomCode:room.code,fromStageIndex:previous.stageIndex,toStageIndex:0,revision:room.revision,startedAt:room.serverNow};
    return this.accept(event,room.serverNow)?event:null;
  }
  observe(room:Baseline):void {
    if(this.baseline?.code!==room.code)this.seen.clear();
    if(!this.baseline || this.baseline.code!==room.code || room.revision>=this.baseline.revision)this.baseline={code:room.code,stageIndex:room.stageIndex,revision:room.revision,phase:room.phase};
  }
  accept(event:AgeTransitionEvent,now:number):boolean {
    const b=this.baseline;
    if(!event || !b || typeof event.id!=='string' || !event.id || event.id.length>120 || this.seen.has(event.id) || event.roomCode!==b.code)return false;
    if(!Number.isSafeInteger(event.toStageIndex) || event.toStageIndex<0 || event.toStageIndex>5 || event.fromStageIndex!==b.stageIndex || event.toStageIndex!==b.stageIndex+1 || !Number.isSafeInteger(event.revision) || event.revision<=b.revision)return false;
    // A slow live connection should still see its chapter. Connection
    // baselines and revisions reject replay; network latency is not a replay.
    if(!Number.isFinite(event.startedAt) || event.startedAt<=0 || event.startedAt-now>5000)return false;
    if(b.phase!=='questions' && !(b.phase==='initial-selection' && event.toStageIndex===0))return false;
    this.seen.add(event.id);return true;
  }
}
